import React, {useCallback, useEffect, useRef, useState} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    GridActionsCellItem,
    GridRowEditStopReasons,
    GridRowModes,
    GridToolbarContainer
} from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import {
    Alert,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText, DialogActions, Paper, Typography
} from '@mui/material';
import axiosInstance from '../AxiosInstance';
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import DeleteIcon from "@mui/icons-material/Delete";
import Snackbar from '@mui/material/Snackbar';
import {
    isGreaterThanZero,
    validateString
} from "../Utilities/validation";
import Draggable from "react-draggable";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import WarningIcon from '@mui/icons-material/Warning';
import LinearProgress from '@mui/material/LinearProgress';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { useColumnWidths } from '../Utilities/columnWidthUtils';
import XeroCustomerContactDetailsPanel from './XeroCustomerContactDetailsPanel';
import CustomerNotesPanel from './CustomerNotesPanel';

const defaultColumnWidths = {
    name: 200,
    channelId: 120,
    externalCustomerId: 380,
};

/** @see IntegrationProviderEnum.Xero */
const INTEGRATION_PROVIDER_XERO = 3;
const UNLINKED_INTEGRATION_LABEL = 'Unlinked';
const NONE_INTEGRATION_OPTION_ID = -1;
const CLEARED_INTEGRATION_OPTION_ID = -2;

/** Display/sort label for the Integration column (provider name, or Unlinked). */
function getIntegrationDisplayValue(row) {
    return row?.integrationProviderName || UNLINKED_INTEGRATION_LABEL;
}

/** Lower rank sorts first: unlinked customers before linked ones. */
function getIntegrationSortRank(row) {
    return row?.integrationProviderName ? 1 : 0;
}

function compareIntegrationProviders(_value1, _value2, cellParams1, cellParams2) {
    const row1 = cellParams1.api.getRow(cellParams1.id);
    const row2 = cellParams2.api.getRow(cellParams2.id);
    const rankDiff = getIntegrationSortRank(row1) - getIntegrationSortRank(row2);
    if (rankDiff !== 0) {
        return rankDiff;
    }
    return getIntegrationDisplayValue(row1).localeCompare(getIntegrationDisplayValue(row2));
}

function PaperComponent(props) {
    return (
        <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} />
        </Draggable>
    );
}

function EditToolbar(props) {
    const {
        customers,
        setCustomers,
        setRowModesModel,
        selectedCustomer,
        setSnackbar,
        handleOpenConfirmationDialog
    } = props;

    const handleClick = () => {
        const hasUnsavedRow = customers.some(row => row.isNew === true);
        if (hasUnsavedRow) {
            setSnackbar({
                children: "Please save or cancel the existing new customer first.",
                severity: "warning"
            });
            return;
        }

        const customerId = -1;
        const newRow = {
            customerId,
            name: '',
            channelId: null,
            isIntegrationIgnored: false,
            externalCustomerId: null,
            integrationProviderName: null,
            isNew: true,
            canDelete: true
        };

        setCustomers((oldRows) => [newRow, ...oldRows]);

        setRowModesModel((oldModel) => ({
            [customerId]: {mode: GridRowModes.Edit, fieldToFocus: 'name'},
            ...oldModel,
        }));

        setTimeout(() => {
            try {
                const nameInput = document.querySelector(`[data-id="${customerId}"] [data-field="name"] input`);
                if (nameInput) {
                    nameInput.focus();
                }
            } catch (error) {
                console.log('Focus failed:', error);
            }
        }, 200);
    };

    const handleDeleteClick = () => {
        if (!selectedCustomer || !selectedCustomer.customerId) {
            console.error("No customer selected for deletion.");
            setSnackbar({children: "No customer selected", severity: "error"});
            return;
        }
        handleOpenConfirmationDialog();
    };

    return (
        <GridToolbarContainer sx={{ justifyContent: 'flex-end', gap: 1 }}>
            <Tooltip title="Create">
                <IconButton aria-label="create" onClick={handleClick}>
                    <AddIcon />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton aria-label="delete" onClick={handleDeleteClick}>
                    <DeleteIcon />
                </IconButton>
            </Tooltip>
        </GridToolbarContainer>
    );
}

const CustomerTable = () => {

    const [searchParams, setSearchParams] = useSearchParams();
    const [customers, setCustomers] = useState([]);
    const [channels, setChannels] = useState([]);
    const [externalCustomers, setExternalCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [rowModesModel, setRowModesModel] = useState({});
    const [snackbar, setSnackbar] = useState(null);
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
    const [hasIntegrationProviders, setHasIntegrationProviders] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingExternalCustomers, setLoadingExternalCustomers] = useState(false);
    const [xeroContactDetails, setXeroContactDetails] = useState({});
    const [loadingXeroContactDetails, setLoadingXeroContactDetails] = useState({});
    const [organisationHasXeroIntegration, setOrganisationHasXeroIntegration] = useState(false);
    const { columnWidths, handleColumnResize } = useColumnWidths('customer', defaultColumnWidths);
    const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState(() => new Set());
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [noteDialogBody, setNoteDialogBody] = useState({ title: '', text: '', loading: false });
    const [reassignConfirmDialogOpen, setReassignConfirmDialogOpen] = useState(false);
    const [reassignConfirmDetails, setReassignConfirmDetails] = useState(null);
    const reassignConfirmResolverRef = useRef(null);

    const getIntegrationProviderName = (externalCustomerId) => {
        if (!hasIntegrationProviders || !isGreaterThanZero(externalCustomerId)) {
            return null;
        }
        
        const selectedExternal = externalCustomers.find(
            ext => ext.externalCustomerId === externalCustomerId
        );
        
        return selectedExternal ? selectedExternal.provider : null;
    };

    const handleServerError = (error, customerId, isUpdate = true) => {
        console.error(`Error ${isUpdate ? 'updating' : 'creating'} the customer:`, error);

        if (error.response?.status === 500) {
            const originalRow = customers.find(row => row.customerId === customerId) || {
                customerId,
                externalCustomerId: null,
                name: '',
                channelId: null,
                    isIntegrationIgnored: false
            };
            
            setSnackbar({
                children: `There was an error ${isUpdate ? 'updating' : 'creating'} this customer, please try again.`,
                severity: 'error'
            });
            
            return originalRow;
        }
        
        let errorMessage = error.response?.data || `Error ${isUpdate ? 'updating' : 'creating'} customer`;
        throw new Error(errorMessage);
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            await Promise.all([
                fetchChannels(),
                fetchCustomers(),
                checkIntegrationProviders(),
                fetchOrganisationProviderLock()
            ]);
            setLoading(false);
        };
        
        fetchInitialData();
    }, []);

    const fetchChannels = async () => {
        try {
            const response = await axiosInstance.get('/channels');
            setChannels(response.data);
        } catch (error) {
            console.error('Error fetching channels:', error);
        }
    };

    const fetchCustomers = async () => {
        try {
            const response = await axiosInstance.get('/customers');
            
            if (!Array.isArray(response.data)) {
                console.error('Expected an array of customers but received:', response.data);
                setSnackbar({
                    children: 'Error loading customers: Invalid data format',
                    severity: 'error'
                });
                return [];
            }
            
            const validCustomers = response.data
                .filter(customer => 
                    customer && 
                    typeof customer === 'object' && 
                    'customerId' in customer
                )
                .map(customer => ({
                    ...customer,
                    isIntegrationIgnored: customer.isIntegrationIgnored ?? false
                }));
                
            setCustomers(validCustomers);
            
            if (validCustomers.length !== response.data.length) {
                console.warn(`Filtered out ${response.data.length - validCustomers.length} invalid customer records`);
            }

            return validCustomers;
        } catch (error) {
            console.error('Error fetching customers:', error);
            setSnackbar({
                children: 'Error loading customers',
                severity: 'error'
            });
            return [];
        }
    };

    const fetchExternalCustomers = async () => {
        if (!hasIntegrationProviders) {
            setExternalCustomers([]);
            return;
        }
        
        setLoadingExternalCustomers(true);
        try {
            const response = await axiosInstance.get('/customers/externalunlinked', {
                params: {
                    includeLinked: true
                }
            });
            setExternalCustomers(response.data);
        } catch (error) {
            console.error('Error fetching external customers:', error);
        } finally {
            setLoadingExternalCustomers(false);
        }
    };

    // todo : probably need a dedicated endpoint for this
    const checkIntegrationProviders = async () => {
        try {
            const response = await axiosInstance.get('/settings');
            setHasIntegrationProviders(
                response.status === 200 && response.data?.isEnabled === true
            );
        } catch (error) {
            if (error.response && error.response.status === 404) {
                setHasIntegrationProviders(false);
            } else {
                console.error('Error checking integration providers:', error);
                setHasIntegrationProviders(false);
            }
        }
    };

    const fetchOrganisationProviderLock = async () => {
        try {
            const response = await axiosInstance.get('/settings/providers');
            const lockedId = response.data?.lockedProviderId;
            setOrganisationHasXeroIntegration(!!response.data?.hasXeroIntegration || lockedId === INTEGRATION_PROVIDER_XERO);
        } catch (error) {
            console.error('Error fetching integration providers:', error);
            setOrganisationHasXeroIntegration(false);
        }
    };

    const handleCloseSnackbar = () => setSnackbar(null);

    const handleRowModesModelChange = (newRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const handleRowEditStop = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }

        if (params.reason === GridRowEditStopReasons.escapeKeyDown) {
            const id = params.id;
            const editedRow = customers.find((row) => row.customerId === id);
            if (editedRow && editedRow.customerId === -1) {
                setCustomers(customers.filter((row) => row.customerId !== -1));
            }
        }
    };

    const handleProcessRowUpdateError = useCallback((error) => {
        setSnackbar({children: error.message, severity: 'error'});
    }, []);

    const fetchXeroContactDetails = useCallback(async (customerId) => {
        if (Object.prototype.hasOwnProperty.call(xeroContactDetails, customerId) || loadingXeroContactDetails[customerId]) {
            return;
        }

        setLoadingXeroContactDetails((current) => ({
            ...current,
            [customerId]: true
        }));

        try {
            const response = await axiosInstance.get(`/customers/${customerId}/xero-contact`);
            setXeroContactDetails((current) => ({
                ...current,
                [customerId]: response.data
            }));
        } catch (error) {
            if (error.response?.status === 404) {
                setXeroContactDetails((current) => ({
                    ...current,
                    [customerId]: null
                }));
                return;
            }

            console.error('Error fetching Xero contact details:', error);
            setSnackbar({
                children: 'Error loading Xero contact details',
                severity: 'error'
            });
        } finally {
            setLoadingXeroContactDetails((current) => ({
                ...current,
                [customerId]: false
            }));
        }
    }, [loadingXeroContactDetails, xeroContactDetails]);

    const handlePromotionRowClick = (params) => {
        if (params.row.isNew) {
            setSelectedCustomer(null);
            return;
        }

        setSelectedCustomer(params.row);
    };

    const handleDetailPanelExpandedRowIdsChange = useCallback((expandedRowIds) => {
        setDetailPanelExpandedRowIds(new Set(expandedRowIds));
        expandedRowIds.forEach((rowId) => {
            const customer = customers.find((row) => row.customerId === rowId);
            if (
                organisationHasXeroIntegration &&
                customer?.integrationProviderName === 'Xero'
            ) {
                fetchXeroContactDetails(customer.customerId);
            }
        });
    }, [customers, fetchXeroContactDetails, organisationHasXeroIntegration]);

    useEffect(() => {
        const nid = searchParams.get('customerNoteId');
        const cid = searchParams.get('customerId');
        if (!nid || !cid) {
            return;
        }
        const customerIdNum = Number(cid);
        const noteIdNum = Number(nid);
        if (Number.isNaN(customerIdNum) || Number.isNaN(noteIdNum)) {
            return;
        }

        setDetailPanelExpandedRowIds((prev) => new Set([...prev, customerIdNum]));
        setNoteDialogOpen(true);
        setNoteDialogBody({ title: 'Customer note', text: '', loading: true });

        let cancelled = false;
        axiosInstance
            .get(`/customers/${customerIdNum}/notes`)
            .then((res) => {
                if (cancelled) return;
                const note = (res.data || []).find((n) => n.customerNoteId === noteIdNum);
                const customerRow = customers.find((c) => c.customerId === customerIdNum);
                const name = customerRow?.name || `Customer #${customerIdNum}`;
                setNoteDialogBody({
                    title: `Note — ${name}`,
                    text: note ? note.text : 'This note could not be found.',
                    loading: false
                });
            })
            .catch(() => {
                if (!cancelled) {
                    setNoteDialogBody({
                        title: 'Customer note',
                        text: 'Could not load note.',
                        loading: false
                    });
                }
            });

        return () => {
            cancelled = true;
        };
    }, [searchParams, customers]);

    const closeNoteDialog = () => {
        setNoteDialogOpen(false);
        setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            p.delete('customerNoteId');
            p.delete('customerId');
            return p;
        }, { replace: true });
    };

    const handleEditClick = (id) => () => {
        if (hasIntegrationProviders) {
            fetchExternalCustomers();
        }
        setRowModesModel((prev) => ({...prev, [id]: {mode: GridRowModes.Edit}}));
    };

    const handleSaveClick = (id) => () => {
        setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});
    };

    const handleCancelClick = (id) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: {mode: GridRowModes.View, ignoreModifications: true},
        });

        const editedRow = customers.find((row) => row.customerId === id);
        if (editedRow.isNew) {
            setCustomers(customers.filter((row) => row.customerId !== id));
        }
    };

    const handleOpenConfirmationDialog = (type) => {
        setOpenConfirmationDialog(true);
    };

    const handleCloseConfirmationDialog = () => {
        setOpenConfirmationDialog(false);
    };

    const promptReassignConfirmation = (details) => {
        return new Promise((resolve) => {
            reassignConfirmResolverRef.current = resolve;
            setReassignConfirmDetails(details);
            setReassignConfirmDialogOpen(true);
        });
    };

    const resolveReassignConfirmation = (confirmed) => {
        setReassignConfirmDialogOpen(false);
        setReassignConfirmDetails(null);
        if (reassignConfirmResolverRef.current) {
            reassignConfirmResolverRef.current(confirmed);
            reassignConfirmResolverRef.current = null;
        }
    };

    const handleConfirmDeleteCustomer = async () => {
        if (!selectedCustomer) {
            console.error("Attempt to delete without selecting a customer.");
            setSnackbar({children: "No customer selected for deletion", severity: "error"});
            return;
        }

        setLoading(true);
        try {
            const response = await axiosInstance.delete(`/customers/${selectedCustomer.customerId}`);
            if (response.status === 204 || response.status === 200) {
                setCustomers((currentCustomers) => currentCustomers.filter(row => row.customerId !== selectedCustomer.customerId));
                setSnackbar({children: "Customer successfully deleted", severity: "success"});
            }
        } catch (error) {
            console.error(`Error deleting the customer: ${error}`);
            setSnackbar({children: `Error deleting the customer: ${error.response?.data || error.message}`, severity: "error"});
        } finally {
            setSelectedCustomer(null);
            handleCloseConfirmationDialog();
            setLoading(false);
        }
    };


    const processRowUpdate = async (newRow, oldRow) => {
        setLoading(true);
        
        try {
            const normalizeLinkSelection = (row) => {
                if (!hasIntegrationProviders) {
                    return {
                        ...row,
                        externalCustomerId: null,
                        isIntegrationIgnored: row.isIntegrationIgnored ?? false
                    };
                }

                if (row.externalCustomerId === NONE_INTEGRATION_OPTION_ID) {
                    return {
                        ...row,
                        externalCustomerId: null,
                        isIntegrationIgnored: true
                    };
                }

                if (row.externalCustomerId === CLEARED_INTEGRATION_OPTION_ID) {
                    return {
                        ...row,
                        externalCustomerId: null,
                        isIntegrationIgnored: false
                    };
                }

                if (isGreaterThanZero(row.externalCustomerId)) {
                    return {
                        ...row,
                        externalCustomerId: row.externalCustomerId,
                        isIntegrationIgnored: false
                    };
                }

                return {
                    ...row,
                    externalCustomerId: null,
                    isIntegrationIgnored: row.isIntegrationIgnored ?? false
                };
            };

            const normalizedRow = normalizeLinkSelection(newRow);
            const updatedRow = {
                ...normalizedRow,
                isNew: false
            };

            if (!validateString(normalizedRow.name)) {
                throw new Error('Please enter a valid value for customer name');
            }

            const linkedCustomerForSelectedExternal = isGreaterThanZero(normalizedRow.externalCustomerId)
                ? customers.find(customer =>
                    customer.customerId !== normalizedRow.customerId &&
                    customer.externalCustomerId === normalizedRow.externalCustomerId)
                : null;

            if (linkedCustomerForSelectedExternal) {
                const selectedExternalCustomer = externalCustomers.find(
                    customer => customer.externalCustomerId === normalizedRow.externalCustomerId
                );
                const externalCustomerName = selectedExternalCustomer
                    ? `${selectedExternalCustomer.name} (${selectedExternalCustomer.provider})`
                    : 'the selected integration customer';

                const confirmed = await promptReassignConfirmation({
                    externalCustomerName,
                    linkedCustomerName: linkedCustomerForSelectedExternal.name,
                    targetCustomerName: normalizedRow.name
                });

                if (!confirmed) {
                    return oldRow ??
                        customers.find(row => row.customerId === normalizedRow.customerId) ??
                        { ...newRow, externalCustomerId: null, isIntegrationIgnored: false };
                }
            }

            if (isGreaterThanZero(normalizedRow.customerId)) {
                try {
                    const customerToUpdate = {
                        name: normalizedRow.name,
                        channelId: normalizedRow.channelId,
                        isIntegrationIgnored: normalizedRow.isIntegrationIgnored ?? false,
                        externalCustomerId: normalizedRow.externalCustomerId ?? null
                    };
                    
                    await axiosInstance.put(`/customers/${normalizedRow.customerId}`, customerToUpdate);

                    const refreshedCustomers = await fetchCustomers();
                    const finalUpdatedRow = refreshedCustomers.find(
                        row => row.customerId === normalizedRow.customerId
                    ) ?? {
                        ...updatedRow,
                        integrationProviderName: getIntegrationProviderName(normalizedRow.externalCustomerId)
                    };
                    
                    setSnackbar({children: 'Customer successfully updated', severity: 'success'});
                    
                    if (window.refreshNotifications)
                        window.refreshNotifications();
                    
                    return finalUpdatedRow;
                } catch (error) {
                    return handleServerError(error, normalizedRow.customerId, true);
                }
            } else {
                try {
                    const customerToCreate = {
                        name: normalizedRow.name,
                        channelId: normalizedRow.channelId,
                        isIntegrationIgnored: normalizedRow.isIntegrationIgnored ?? false,
                        externalCustomerId: normalizedRow.externalCustomerId ?? null
                    };
                    
                    const response = await axiosInstance.post(`/customers`, customerToCreate);
                    
                    const integrationProviderName = getIntegrationProviderName(normalizedRow.externalCustomerId);
                    
                    const newCustomer = response.status === 204 ? 
                        { 
                            ...customerToCreate, 
                            customerId: Date.now(),
                            integrationProviderName 
                        } : 
                        { ...response.data, integrationProviderName, isIntegrationIgnored: response.data.isIntegrationIgnored ?? false };
                    
                    setCustomers(prevCustomers =>
                        prevCustomers.map(row =>
                            row.customerId === newRow.customerId ? newCustomer : row
                        )
                    );

                    setSnackbar({children: 'Customer successfully created', severity: 'success'});
                    
                    if (window.refreshNotifications)
                        window.refreshNotifications();
                    
                    return newCustomer;
                } catch (error) {
                    return handleServerError(error, normalizedRow.customerId, false);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    /** Shared `size="small"` outlined control (~40px) for Name, Channel, and Integration edit cells */
    const gridEditOutlinedInputSx = {
        width: '100%',
        minWidth: 0,
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
            minHeight: 40,
            height: 40,
            boxSizing: 'border-box',
            paddingTop: 0,
            paddingBottom: 0,
            alignItems: 'center',
        },
        '& .MuiOutlinedInput-input': {
            py: 0,
            boxSizing: 'border-box',
        },
    };

    const renderEditNameCell = (params) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                }}
            >
                <TextField
                    value={params.value}
                    onChange={(event) => {
                        const value = event.target.value;
                        if (value.length <= 100) {
                            params.api.setEditCellValue({ id: params.id, field: params.field, value }, event);
                        }
                    }}
                    slotProps={{ htmlInput: { maxLength: 100 } }}
                    size="small"
                    variant="outlined"
                    margin="none"
                    hiddenLabel
                    fullWidth
                    sx={gridEditOutlinedInputSx}
                />
            </Box>
        );
    };

    const renderEditChannelCell = (params) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                }}
            >
                <TextField
                    select
                    value={params.value ?? ''}
                    onChange={(event) => {
                        const raw = event.target.value;
                        const value = raw === '' ? null : Number(raw);
                        params.api.setEditCellValue(
                            { id: params.id, field: params.field, value },
                            event
                        );
                    }}
                    size="small"
                    variant="outlined"
                    margin="none"
                    hiddenLabel
                    fullWidth
                    sx={gridEditOutlinedInputSx}
                    slotProps={{
                        select: {
                            MenuProps: {
                                disableScrollLock: true,
                            },
                        },
                    }}
                >
                    <MenuItem value="">
                        Uncategorised
                    </MenuItem>
                    {channels.map((channel) => (
                        <MenuItem key={channel.channelId} value={channel.channelId}>
                            {channel.path || channel.name}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>
        );
    };

    const renderIntegrationCell = (params) => {
        const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
        if (isInEditMode)
            return null;

        const displayValue = getIntegrationDisplayValue(params.row);
        if (displayValue !== UNLINKED_INTEGRATION_LABEL) {
            return displayValue;
        }

        if (params.row.isIntegrationIgnored)
            return 'None';

        return (
            <Tooltip title={UNLINKED_INTEGRATION_LABEL}>
                <WarningIcon color="warning" />
            </Tooltip>
        );
    };

    const renderEditIntegrationCell = (params) => {
        if (loadingExternalCustomers) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', padding: '0 10px' }}>
                    <CircularProgress size={20} />
                </Box>
            );
        }

        const noneOption = {
            externalCustomerId: NONE_INTEGRATION_OPTION_ID,
            name: 'None',
            provider: '',
            isLinked: false,
            linkedCustomerName: null,
        };
        const externalCustomerOptions = [noneOption, ...externalCustomers];
        const getExternalCustomerOptionLabel = (option) => (
            option.externalCustomerId === NONE_INTEGRATION_OPTION_ID
                ? 'None'
                : `${option.name} (${option.provider})`
        );

        const rowExternalId = params.row.externalCustomerId ?? null;
        const isNoneSelected = rowExternalId === NONE_INTEGRATION_OPTION_ID ||
            (params.row.isIntegrationIgnored && rowExternalId == null);
        const selectedOption = isNoneSelected
            ? noneOption
            : rowExternalId == null || rowExternalId === CLEARED_INTEGRATION_OPTION_ID
                ? null
                : externalCustomers.find((o) => o.externalCustomerId === rowExternalId) ?? null;

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                }}
            >
                <Autocomplete
                    size="small"
                    fullWidth
                    options={externalCustomerOptions}
                    getOptionKey={(option) => option.externalCustomerId}
                    getOptionLabel={getExternalCustomerOptionLabel}
                    isOptionEqualToValue={(a, b) => a.externalCustomerId === b.externalCustomerId}
                    value={selectedOption}
                    onChange={(event, newValue) => {
                        const isNone = newValue?.externalCustomerId === NONE_INTEGRATION_OPTION_ID;
                        const isCleared = newValue == null;
                        params.api.setEditCellValue({
                            id: params.id,
                            field: 'externalCustomerId',
                            value: isNone
                                ? NONE_INTEGRATION_OPTION_ID
                                : isCleared
                                    ? CLEARED_INTEGRATION_OPTION_ID
                                    : newValue.externalCustomerId,
                        });
                    }}
                    clearOnEscape
                    renderOption={(optionProps, option) => (
                        <Box
                            component="li"
                            {...optionProps}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 0.25,
                                width: '100%',
                                textAlign: 'left'
                            }}
                        >
                            {option.externalCustomerId === NONE_INTEGRATION_OPTION_ID ? (
                                <Typography component="span" variant="body2" sx={{ width: '100%', textAlign: 'left' }}>
                                    None
                                </Typography>
                            ) : (
                                <>
                            <Typography
                                component="span"
                                variant="body2"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', textAlign: 'left' }}
                            >
                                {`${option.name} (${option.provider})`}
                            </Typography>
                            <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                    color: option.isLinked ? 'warning.main' : 'success.main',
                                    whiteSpace: 'nowrap',
                                    width: '100%',
                                    textAlign: 'left',
                                }}
                            >
                                {option.isLinked
                                    ? option.linkedCustomerName ?? 'Linked'
                                    : 'Unlinked'}
                            </Typography>
                                </>
                            )}
                        </Box>
                    )}
                    renderInput={(acParams) => {
                        const { sx: acSx, ...acRest } = acParams;
                        return (
                            <TextField
                                {...acRest}
                                placeholder="Search integration customers…"
                                variant="outlined"
                                margin="none"
                                hiddenLabel
                                fullWidth
                                size="small"
                                sx={[gridEditOutlinedInputSx, ...(acSx ? [acSx] : [])]}
                            />
                        );
                    }}
                    slotProps={{
                        listbox: {
                            sx: {
                                maxHeight: 280,
                                '& .MuiAutocomplete-option': {
                                    justifyContent: 'flex-start',
                                },
                            },
                        },
                    }}
                    sx={{
                        ...gridEditOutlinedInputSx,
                        flex: '1 1 auto',
                        minWidth: 0,
                        maxWidth: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        alignSelf: 'center',
                        '& .MuiInputBase-input': {
                            textAlign: 'left',
                        },
                        '& .MuiAutocomplete-input': {
                            textAlign: 'left',
                        },
                    }}
                />
            </Box>
        );
    };

    const baseColumns = [
        {
            field: 'name',
            headerName: 'Name',
            width: columnWidths.name,
            editable: true,
            renderEditCell: renderEditNameCell,
        },
        {
            field: 'channelId',
            headerName: 'Channel',
            width: columnWidths.channelId,
            editable: true,
            renderEditCell: renderEditChannelCell,
            valueFormatter: (value) => {
                const channel = channels.find(f => f.channelId === value);
                return channel ? (channel.path || channel.name) : 'Uncategorised';
            },
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({id}) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon/>}
                            label="Save"
                            sx={{
                                color: 'primary.main',
                            }}
                            onClick={handleSaveClick(id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon/>}
                            label="Cancel"
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />,
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon/>}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />
                ];
            },
        },
    ];
    
    const integrationColumn = {
        field: 'externalCustomerId',
        headerName: 'Integration',
        description: 'Customers can be linked to 3rd party integrations or set to None.',
        width: columnWidths.externalCustomerId,
        minWidth: 380,
        editable: true,
        sortComparator: compareIntegrationProviders,
        renderCell: renderIntegrationCell,
        renderEditCell: renderEditIntegrationCell,
    };
    
    const columns = hasIntegrationProviders ? 
        [...baseColumns.slice(0, 2), integrationColumn, ...baseColumns.slice(2)] : 
        baseColumns;

    const getDetailPanelContent = useCallback(({ row }) => {
        const xeroBlock =
            organisationHasXeroIntegration && row.integrationProviderName === 'Xero' ? (
                <XeroCustomerContactDetailsPanel
                    loading={!!loadingXeroContactDetails[row.customerId]}
                    detail={xeroContactDetails[row.customerId]}
                />
            ) : null;

        return (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                {xeroBlock}
                <CustomerNotesPanel
                    customerId={row.customerId}
                    customerName={row.name}
                    onNotesChanged={() => {
                        if (window.refreshNotifications) window.refreshNotifications();
                    }}
                />
            </Box>
        );
    }, [loadingXeroContactDetails, xeroContactDetails, organisationHasXeroIntegration]);

    return (
        <Box sx={{ height: '100%', width: '100%' }}>
            <DataGridPro
                key={`customer-grid-${hasIntegrationProviders}-${customers.length}`}
                pagination
                paginationMode="client"
                getRowId={(row) => row.customerId}
                rows={customers}
                columns={columns}
                loading={loading}
                editMode="row"
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                onColumnResize={handleColumnResize}
                slots={{
                    toolbar: EditToolbar,
                    loadingOverlay: LinearProgress,
                }}
                slotProps={{
                    toolbar: {
                        customers,
                        setCustomers,
                        setRowModesModel,
                        selectedCustomer,
                        setSnackbar,
                        handleOpenConfirmationDialog
                    }
                }}
                onRowClick={handlePromotionRowClick}
                getDetailPanelContent={getDetailPanelContent}
                getDetailPanelHeight={() => 'auto'}
                detailPanelExpandedRowIds={[...detailPanelExpandedRowIds]}
                onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
                initialState={{
                    pagination: {
                        paginationModel: { pageSize: 25, page: 0 }
                    },
                    ...(hasIntegrationProviders && {
                        sorting: {
                            sortModel: [{ field: 'externalCustomerId', sort: 'asc' }]
                        }
                    })
                }}
                pageSizeOptions={[25, 50, 100]}
                sx={{
                    '& .MuiDataGrid-row--editing .MuiDataGrid-cell--editing:not([data-field="actions"])': {
                        display: 'flex',
                        alignItems: 'center',
                        py: 0,
                    },
                }}
            />
            {!!snackbar && (
                <Snackbar
                    open
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                    onClose={handleCloseSnackbar}
                    autoHideDuration={6000}
                >
                    <Alert {...snackbar} onClose={handleCloseSnackbar}/>
                </Snackbar>
            )}
            <Dialog
                open={openConfirmationDialog}
                onClose={handleCloseConfirmationDialog}
                PaperComponent={PaperComponent}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle style={{cursor: 'move'}} id="draggable-dialog-title">
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this customer? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmationDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDeleteCustomer} color="primary">
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={reassignConfirmDialogOpen}
                onClose={() => resolveReassignConfirmation(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reassign customer?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        <strong>{reassignConfirmDetails?.externalCustomerName ?? 'This integration customer'}</strong>
                        {' '}is currently linked to{' '}
                        <strong>{reassignConfirmDetails?.linkedCustomerName ?? 'another customer'}</strong>.
                    </DialogContentText>
                    <DialogContentText sx={{ mb: 2 }}>
                        If you continue, it will be moved to{' '}
                        <strong>{reassignConfirmDetails?.targetCustomerName ?? 'this customer'}</strong>.
                    </DialogContentText>
                    <DialogContentText>
                        <strong>{reassignConfirmDetails?.linkedCustomerName ?? 'The currently linked customer'}</strong>{' '}
                        will be set to <strong>None</strong>.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => resolveReassignConfirmation(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={() => resolveReassignConfirmation(true)} color="primary" variant="contained" autoFocus>
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={noteDialogOpen} onClose={closeNoteDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{noteDialogBody.title}</DialogTitle>
                <DialogContent>
                    {noteDialogBody.loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <DialogContentText
                            sx={{
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'break-word',
                                wordBreak: 'break-word',
                            }}
                        >
                            {noteDialogBody.text}
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeNoteDialog} color="primary" autoFocus>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export {
    UNLINKED_INTEGRATION_LABEL,
    getIntegrationDisplayValue,
    getIntegrationSortRank,
    compareIntegrationProviders,
};

export default CustomerTable;
